import { useMemo, useState } from "react";
import "./SalaryRangeSlider.css";

const SCALES = {
  monthly: { step: 1000, max: 200000, initialMin: 20000, initialMax: 80000 },
  semi_monthly: { step: 500, max: 100000, initialMin: 10000, initialMax: 40000 },
  bi_weekly: { step: 500, max: 100000, initialMin: 10000, initialMax: 40000 },
  weekly: { step: 100, max: 50000, initialMin: 5000, initialMax: 20000 },
  daily: { step: 50, max: 10000, initialMin: 500, initialMax: 3000 },
  hourly: { step: 10, max: 2000, initialMin: 50, initialMax: 500 },
};

export function defaultSalaryRange(period = "monthly") {
  const scale = SCALES[period] || SCALES.monthly;
  return { min: String(scale.initialMin), max: String(scale.initialMax) };
}

export function salaryRangeScale(period = "monthly") {
  return SCALES[period] || SCALES.monthly;
}

const pesos = (value) => `₱${Number(value).toLocaleString("en-PH", { maximumFractionDigits: 2 })}`;

export default function SalaryRangeSlider({ minValue = "", maxValue = "", period = "monthly", onChange,
  onEnable, onClear, emptyLabel = "Any salary", label = "Salary range", interactiveWhenEmpty = false,
  bounds }) {
  const scale = SCALES[period] || SCALES.monthly;
  const [extendedCeiling, setExtendedCeiling] = useState(0);
  const bounded = bounds !== undefined;
  const hasBounds = Number.isFinite(Number(bounds?.min)) && Number.isFinite(Number(bounds?.max))
    && Number(bounds.min) > 0 && Number(bounds.max) >= Number(bounds.min);
  const active = minValue !== "" || maxValue !== "";
  const numericMin = Number(minValue);
  const numericMax = Number(maxValue);
  const floor = bounded && hasBounds ? Math.floor(Number(bounds.min) / scale.step) * scale.step : scale.step;
  const ceiling = useMemo(() => {
    if (bounded && hasBounds) return Math.ceil(Number(bounds.max) / scale.step) * scale.step;
    const entered = Math.max(Number(minValue) || 0, Number(maxValue) || 0);
    return Math.max(scale.max, extendedCeiling, Math.ceil((entered * 1.25) / scale.step) * scale.step);
  }, [bounded, hasBounds, bounds?.max, minValue, maxValue, extendedCeiling, scale.max, scale.step]);
  const clamp = (value) => Math.max(floor, Math.min(value, ceiling));
  const snap = (value) => bounded ? Math.round(value / scale.step) * scale.step : value;
  const left = active && numericMin > 0 ? clamp(snap(numericMin)) : bounded && hasBounds ? floor : scale.initialMin;
  const right = active && numericMax > 0 ? clamp(snap(numericMax)) : bounded && hasBounds ? ceiling : scale.initialMax;
  const lower = Math.min(left, right);
  const upper = Math.max(left, right);
  const rangeWidth = ceiling - floor;
  const leftPercent = rangeWidth ? ((lower - floor) / rangeWidth) * 100 : 50;
  const rightPercent = rangeWidth ? ((upper - floor) / rangeWidth) * 100 : 50;
  const amountLabel = !active ? bounded && hasBounds ? `${pesos(floor)} – ${pesos(ceiling)}` : emptyLabel
    : minValue && maxValue ? `${pesos(left)} – ${pesos(right)}`
      : minValue ? `${pesos(left)} and up` : `Up to ${pesos(right)}`;
  const formatSliderValue = (value) => String(snap(value));

  if (bounded && !hasBounds) return <div className="salary-range-control text-xs">
    <p className="font-semibold">{label}</p>
    <p className="mt-2 text-neutral-500">No posted monthly salary ranges yet.</p>
  </div>;

  return <div className={`salary-range-control ${active ? "is-active" : ""}`}>
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
      <span className="font-semibold">{label}</span>
      <span className="font-medium tabular-nums text-neutral-700 dark:text-neutral-200">{amountLabel}</span>
    </div>
    <div className="salary-range-rail" aria-hidden="true">
      <span className="salary-range-selection" style={{ left: `${leftPercent}%`, width: `${rightPercent - leftPercent}%` }} />
    </div>
    <div className="salary-range-inputs">
      <input type="range" min={floor} max={ceiling} step={scale.step} value={lower}
        aria-label="Minimum salary" disabled={ceiling === floor || (!active && !interactiveWhenEmpty)}
        onChange={(event) => onChange(formatSliderValue(Math.min(Number(event.target.value), upper)),
          bounded || !active ? formatSliderValue(upper) : maxValue)} />
      <input type="range" min={floor} max={ceiling} step={scale.step} value={upper}
        aria-label="Maximum salary" disabled={ceiling === floor || (!active && !interactiveWhenEmpty)}
        onChange={(event) => onChange(bounded || !active ? formatSliderValue(lower) : minValue,
          formatSliderValue(Math.max(Number(event.target.value), lower)))} />
    </div>
    {!active ? interactiveWhenEmpty ? <p className="mt-3 text-xs text-neutral-500">Drag the handles to filter</p>
      : <button type="button" className="mt-3 text-xs font-semibold underline underline-offset-2" onClick={onEnable}>Set range</button>
      : <div className="mt-3 flex flex-wrap justify-end gap-3 text-xs">
        {!bounded && upper >= ceiling - scale.step && <button type="button" className="font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
          onClick={() => setExtendedCeiling(ceiling * 2)}>Extend range</button>}
        <button type="button" className="font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-white" onClick={onClear}>Clear</button>
      </div>}
  </div>;
}
